import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { publicApi } from '../utils/publicApi';
import brandIsolationLeft from '../assets/advantageOmanFooterIcons/brand-isolation-left.svg';
import brandDivider from '../assets/advantageOmanFooterIcons/brand-divider.svg';
import footerBrandLockup from '../assets/advantageOmanFooterIcons/footer-brand-lockup.svg';
import ministryWordmark from '../assets/advantageOmanFooterIcons/ministry-wordmark.svg';
import ministryEmblem from '../assets/advantageOmanFooterIcons/ministry-emblem.svg';
import tajawobLogo from '../assets/advantageOmanFooterIcons/tajawob.svg';
import omanCircleLogo from '../assets/advantageOmanFooterIcons/omancircle.svg';
import appStoreBadge from '../assets/advantageOmanFooterIcons/app-store-badge.svg';
import googlePlayBadge from '../assets/advantageOmanFooterIcons/google-play-badge.svg';
import iconEmailAssistance from '../assets/advantageOmanFooterIcons/icon-email-assistance.svg';
import iconPhoneAssistance from '../assets/advantageOmanFooterIcons/icon-phone-assistance.svg';
import iconXTwitter from '../assets/advantageOmanFooterIcons/social-x.svg';
import iconFacebook from '../assets/advantageOmanFooterIcons/social-facebook.svg';
import iconYoutube from '../assets/advantageOmanFooterIcons/social-youtube.svg';
import iconInstagram from '../assets/advantageOmanFooterIcons/social-instagram.svg';
import iconLinkedin from '../assets/advantageOmanFooterIcons/social-linkedin.svg';

const pillClass =
  'inline-flex h-[54px] shrink-0 items-center justify-center gap-1.5 rounded-full px-[18px] text-base font-semibold text-white transition hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50';

const pillStyle = {
  background: 'linear-gradient(90deg, rgba(66, 42, 126, 0.70) 0%, rgba(66, 42, 126, 0.49) 100%)',
  boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
};

const FooterPillChevron = () => (
  <svg width="7" height="12" viewBox="0 0 10 14" fill="none" aria-hidden className="shrink-0 text-white">
    <path
      d="M1.5 1.5L7.5 7L1.5 12.5"
      stroke="currentColor"
      strokeWidth="1.29"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Footer = () => {
  const [appComingSoonOpen, setAppComingSoonOpen] = useState(false);
  const { t, isRTL } = useLanguage();

  useEffect(() => {
    if (!appComingSoonOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setAppComingSoonOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [appComingSoonOpen]);

  const linkClass =
    'text-base font-normal text-white hover:text-white/85 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-sm block w-full text-left';

  const handleDownloadGuide = () => {
    window.location.href = `${publicApi.baseUrl}/api/downloadguide/download`;
  };

  const socialLinks = [
    { href: 'https://twitter.com/investinom', icon: iconXTwitter, label: 'X' },
    { href: 'https://www.facebook.com/investinom', icon: iconFacebook, label: 'Facebook' },
    { href: 'https://www.youtube.com/@invest-oman', icon: iconYoutube, label: 'YouTube' },
    { href: 'https://www.instagram.com/investinom', icon: iconInstagram, label: 'Instagram' },
    { href: 'https://om.linkedin.com/company/investinom', icon: iconLinkedin, label: 'LinkedIn' },
  ];

  const badgeClass = 'h-[38px] w-auto object-contain sm:h-11 md:h-14';
  const appleAlt = t('website.footer.downloadAppStore');
  const googleAlt = t('website.footer.downloadGooglePlay');

  const footerGradient = 'linear-gradient(357deg, #13153B 0%, #0B8EBA 100%)';

  return (
    <footer
      className="relative w-full overflow-hidden px-6 py-5 sm:px-10 xl:px-[140px]"
      style={{
        background: 'linear-gradient(357deg, #13153B 0%, #0B8EBA 100%)',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="mx-auto flex max-w-[1156px] flex-col gap-8 xl:gap-10">
        {/* Top: brand strip (start); Tajawob + Oman circle (end) — store badges only in Download Our App column */}
        <div className="flex flex-col gap-6 border-b border-[#AB9FCA] pb-8 md:pb-10">
          <div
            className="flex w-full max-w-full flex-wrap items-end justify-center gap-x-4 gap-y-4 sm:gap-x-6 md:justify-between md:gap-x-[27px]"
            dir="ltr"
          >
            <span className="sr-only">
              Invest Oman, Advantage Oman Forum, Ministry of Commerce, Industry and Investment Promotion, Tajawob
            </span>
            <div className="flex max-w-full flex-wrap items-end justify-center gap-4 sm:gap-6 md:justify-start md:gap-[27px]">
              <img
                src={brandIsolationLeft}
                alt=""
                className="h-[clamp(40px,8vw,61px)] w-auto shrink-0 object-contain"
              />
              <img
                src={brandDivider}
                alt=""
                className="h-[clamp(36px,7.5vw,54px)] w-auto shrink-0 self-center object-contain opacity-95"
              />
              <img
                src={footerBrandLockup}
                alt="Invest Oman — Advantage Oman Forum"
                className="h-[clamp(40px,8vw,65px)] w-auto max-w-[min(100%,280px)] shrink-0 object-contain sm:max-w-[min(100%,320px)] md:max-w-none"
              />
              <img
                src={brandDivider}
                alt=""
                className="h-[clamp(36px,7.5vw,54px)] w-auto shrink-0 self-center object-contain opacity-95"
              />
              <img
                src={ministryWordmark}
                alt=""
                className="h-[clamp(36px,7vw,53px)] w-auto max-w-[min(100%,240px)] shrink-0 object-contain sm:max-w-[min(100%,280px)] md:max-w-none"
              />
              <img
                src={ministryEmblem}
                alt=""
                className="h-[clamp(40px,8vw,66px)] w-auto shrink-0 object-contain"
              />
            </div>
            <div className="flex shrink-0 items-end justify-center gap-4 sm:gap-6 md:gap-[27px]">
              <img
                src={tajawobLogo}
                alt={t('website.footer.tajawobAlt')}
                className="h-10 w-auto shrink-0 object-contain sm:h-11 md:h-[43px]"
                width={72}
                height={43}
              />
              <img
                src={omanCircleLogo}
                alt={t('website.footer.omancircleAlt')}
                className="h-10 w-auto shrink-0 object-contain sm:h-11 md:h-[43px]"
                width={92}
                height={43}
              />
            </div>
          </div>
        </div>

        {/* Body: column 1–2 per Figma + Need Assistance unchanged */}
        <div className="grid grid-cols-1 gap-10 text-start lg:grid-cols-3 lg:gap-12 xl:justify-between">
          <div className="flex max-w-md flex-col gap-9 lg:max-w-none">
            <button type="button" onClick={handleDownloadGuide} className={pillClass} style={pillStyle}>
              {t('website.footer.booklet')}
              <FooterPillChevron />
            </button>
          </div>

          <div className="flex max-w-md flex-col gap-8 lg:max-w-none">
            <h3 className="text-base font-bold text-white">{t('website.footer.downloadTitle')}</h3>
            <p className="max-w-[378px] text-base font-normal leading-6 text-white">
              {t('website.footer.downloadSubtitle')}
            </p>
            <div className="flex flex-wrap items-center gap-4 sm:gap-[18px]">
              <button
                type="button"
                onClick={() => setAppComingSoonOpen(true)}
                aria-label={appleAlt}
                className="inline-block cursor-pointer rounded-md border-0 bg-transparent p-0 opacity-95 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              >
                <img src={appStoreBadge} alt="" className={badgeClass} width={180} height={57} loading="lazy" decoding="async" />
              </button>
              <button
                type="button"
                onClick={() => setAppComingSoonOpen(true)}
                aria-label={googleAlt}
                className="inline-block cursor-pointer rounded-md border-0 bg-transparent p-0 opacity-95 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              >
                <img src={googlePlayBadge} alt="" className={badgeClass} width={180} height={57} loading="lazy" decoding="async" />
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <h3 className="text-base font-bold text-white">{t('website.footer.needAssistance')}</h3>
            <div className="flex items-center gap-4">
              <div className="relative flex h-12 w-12 shrink-0 items-center justify-center">
                <img src={iconEmailAssistance} alt="" className="h-[21px] w-[21px] object-contain" aria-hidden />
              </div>
              <div className="flex min-w-0 flex-col gap-1 text-start">
                <span className="text-sm font-bold text-white">{t('website.footer.emailShort')}</span>
                <a href="mailto:forum@investoman.om" className={`${linkClass} break-all`}>
                  {t('website.footer.assistanceEmail')}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative flex h-12 w-12 shrink-0 items-center justify-center">
                <img src={iconPhoneAssistance} alt="" className="h-[21px] w-[21px] object-contain" aria-hidden />
              </div>
              <div className="flex flex-col gap-1 text-start">
                <span className="text-sm font-bold text-white">{t('website.footer.phoneShort')}</span>
                <a href="tel:+96880000222" className={linkClass}>
                  {t('website.footer.assistancePhone')}
                </a>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-[22px] pt-1">
              {socialLinks.map(({ href, icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-[26px] w-[26px] items-center justify-center opacity-95 transition-opacity hover:opacity-100"
                >
                  <img src={icon} alt="" className="h-[26px] w-[26px] object-contain" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center border-t border-[#AB9FCA] pt-8 pb-6 text-center sm:pt-9">
          <p className="text-sm font-medium leading-[21px] text-white">{t('website.footer.copyrightLine')}</p>
        </div>
      </div>

      {appComingSoonOpen ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="presentation"
          onClick={() => setAppComingSoonOpen(false)}
        >
          <div className="absolute inset-0 bg-[#0a0d2e]/65 backdrop-blur-[2px]" aria-hidden />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="footer-app-coming-soon-title"
            className="relative w-full max-w-md rounded-2xl border border-white/20 p-8 text-center shadow-xl"
            style={{ background: footerGradient, fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            onClick={(e) => e.stopPropagation()}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <h2 id="footer-app-coming-soon-title" className="text-xl font-bold text-white sm:text-2xl">
              {t('website.footer.appComingSoonTitle')}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-white/90">{t('website.footer.appComingSoonMessage')}</p>
            <button
              type="button"
              onClick={() => setAppComingSoonOpen(false)}
              className="mt-8 inline-flex h-11 min-w-[120px] items-center justify-center rounded-full px-6 text-base font-semibold text-white transition hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              style={pillStyle}
            >
              {t('website.footer.appComingSoonClose')}
            </button>
          </div>
        </div>
      ) : null}
    </footer>
  );
};

export default Footer;
